import { useEffect, useMemo } from "react";
import { Users } from "lucide-react";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { ParentPriority } from "@/lib/parent/currentPriority";
import { buildMentorPulse, type MentorInsight } from "./intelligenceSignals";
import { PREMIUM_PANEL, PanelGlow, SectionHeading } from "./_styles";
import { trackMetaEvent } from "@/lib/metaPixel";
import { useDashboardScope } from "@/hooks/useDashboardScope";
import aelyraImg from "@/assets/instructor-aelyra.png";
import caelionImg from "@/assets/instructor-caelion.png";
import vaelorImg from "@/assets/instructor-vaelor.png";
const lumiAsset = { url: "/assets/lumi-vanta.png" };

const MENTOR_AVATAR: Record<string, string> = {
  "Aelyra Solari": aelyraImg,
  "Caelion Veyr": caelionImg,
  "Headmaster Vaelor": vaelorImg,
  "Lumi Vanta": lumiAsset.url,
};

// Filler/placeholder phrases that must NOT count as real feedback.
const FILLER_PATTERNS = [
  /waiting on/i,
  /preparing insights/i,
  /will weigh in/i,
  /is cheering them on/i,
  /unlocks after/i,
];

function hasMeaningfulFeedback(m: MentorInsight): boolean {
  if (m.state !== "ready") return false;
  const t = (m.insight || "").trim();
  if (t.length < 12) return false;
  return !FILLER_PATTERNS.some((re) => re.test(t));
}

export default function MentorPulse({ analytics, priority }: { analytics: ParentAnalyticsResult; priority?: ParentPriority }) {
  const { scope, isAdmin } = useDashboardScope();
  const all = useMemo(() => buildMentorPulse(analytics), [analytics]);
  const scoped = useMemo(() => {
    if (isAdmin || scope === "full") return all;
    return all.map((m) => {
      if (m.domain === "Entrepreneurship") {
        return { ...m, insight: "Advanced ownership guidance unlocks after launch.", state: "building" as const };
      }
      if (m.domain === "Finance & Investing") {
        return { ...m, domain: "Money & Credit Foundations" };
      }
      return m;
    });
  }, [all, scope, isAdmin]);
  const baseMentors = useMemo(() => scoped.filter(hasMeaningfulFeedback), [scoped]);
  // Inject a single priority-aware mentor read at the top so the pulse aligns
  // with the current parent priority instead of feeling like a generic feed.
  const mentors = useMemo<MentorInsight[]>(() => {
    if (!priority) return baseMentors;
    const pri: MentorInsight = {
      mentor: priority.mentor.name,
      domain: `${priority.moduleLabel} · ${priority.label}`,
      insight: priority.mentor.insight,
      state: "ready",
    };
    // De-dupe: if the same mentor already appears, replace their entry.
    const filtered = baseMentors.filter((m) => m.mentor !== pri.mentor);
    return [pri, ...filtered];
  }, [baseMentors, priority]);

  useEffect(() => {
    void trackMetaEvent("mentor_pulse_viewed", { customData: { mentors: mentors.length } });
  }, [mentors.length]);

  return (
    <section className={PREMIUM_PANEL}>
      <PanelGlow />
      <div className="relative">
        <SectionHeading
          eyebrow="Mentor Pulse"
          title="The HeirWise mentor team weighs in"
          subtitle="Short reads from each mentor, tied to your teen's recent learning."
          icon={Users}
        />
        {mentors.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.05] bg-background/30 p-5 text-sm leading-relaxed text-muted-foreground">
            Mentor insights are building. As your teen completes more lessons, the mentor team will surface specific guidance here.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {mentors.map((m) => {
              const avatar = MENTOR_AVATAR[m.mentor];
              return (
                <div key={m.mentor} className="rounded-2xl border border-white/[0.05] bg-gradient-to-br from-background/40 to-background/10 p-4">
                  <div className="flex items-start gap-3">
                    {avatar && (
                      <span className="relative inline-flex h-12 w-12 shrink-0">
                        <span className="absolute inset-0 rounded-full bg-primary/20 blur-md" aria-hidden />
                        <img
                          src={avatar}
                          alt={m.mentor}
                          className="relative z-10 h-full w-full rounded-full border border-primary/30 object-cover"
                          loading="lazy"
                        />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-[15px] font-semibold text-foreground truncate">{m.mentor}</p>
                      <p className="mt-0.5 text-[11px] uppercase tracking-[0.16em] text-primary/80 truncate">{mentorRole(m.mentor)}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{m.domain}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-[14px] leading-relaxed text-foreground/95">{m.insight}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function mentorRole(name: string): string {
  switch (name) {
    case "Lumi Vanta":
      return "AI Companion";
    case "Aelyra Solari":
      return "Money & Markets Guide";
    case "Caelion Veyr":
      return "Economics Guide";
    case "Headmaster Vaelor":
      return "Headmaster";
    default:
      return "HeirWise Mentor";
  }
}